import React from 'react';
import { CheckCircle2, CircleDashed, XCircle } from 'lucide-react';
import { SectionCard } from '../../common/SectionCard';
import { EmptyState } from '../../common/EmptyState';
import { formatAed, formatDate } from '../../../utils/format';
import type { ApprovalDetail } from '../../../types/domain';
const statusIcon = {
  signed: CheckCircle2,
  pending: CircleDashed,
  blocked: XCircle
};
const statusClasses = {
  signed: 'text-uae-leaf',
  pending: 'text-uae-bronze',
  blocked: 'text-uae-red'
};
export function ApprovalPanel({
  approval
}: {
  approval?: ApprovalDetail;
}) {
  if (!approval) {
    return <EmptyState title="Approval record not opened" description="The approval record is created when the Bid gate clears and the funding mandate request is lodged." data-id="element-809" data-mp="36hfy5iir46ju" />;
  }
  return <div className="space-y-4" data-id="element-810" data-mp="2dyshcfasfdok">
      <div className="grid gap-4 lg:grid-cols-3" data-id="element-811" data-mp="2dy0kl15gf9wp">
        <SectionCard title="Funding mandate" data-id="element-812" data-mp="291s8jwmjzwtr">
          <p className="font-mono text-xl font-semibold" data-id="element-813" data-mp="28ywckdu9ni7y">{formatAed(approval.mandateAed)}</p>
          <p className="mt-1 text-[12px] text-muted-foreground" data-id="element-814" data-mp="29691sb0brcr8">
            Federal envelope requested for the full lifecycle
          </p>
        </SectionCard>
        <SectionCard title="Committee date" data-id="element-815" data-mp="2iz7pzrrwt1ab">
          <p className="text-xl font-semibold" data-id="element-816" data-mp="2j0itrrlcu91f">{formatDate(approval.committeeDate)}</p>
          <p className="mt-1 text-[12px] text-muted-foreground" data-id="element-817" data-mp="2myyroqiz51tm">
            Decision record captured at the session
          </p>
        </SectionCard>
        <SectionCard title="Decision" data-id="element-818" data-mp="1v7sk5p0xc1ub">
          <p className="text-xl font-semibold capitalize" data-id="element-819" data-mp="1v8u8uwav4mui">{approval.decision}</p>
          <p className="mt-1 text-[12px] text-muted-foreground" data-id="element-820" data-mp="1z9e5zzvfrqgn">
            {approval.decision === 'pending' ? 'Awaiting committee decision' : approval.decision === 'conditional' ? 'Approved subject to recorded conditions' : 'Approved with mandate issued'}
          </p>
        </SectionCard>
      </div>

      <SectionCard title="Federal stakeholder alignment" description="Mandatory sign-offs required before the approval gate can clear" bodyClassName="p-0" data-id="element-821" data-mp="1flmw2ux4qby5">
        <ul className="divide-y divide-border" data-id="element-822" data-mp="1pkc661isas3u">
          {approval.stakeholders.map(stakeholder => {
          const Icon = statusIcon[stakeholder.status];
          return <li key={stakeholder.id} className="flex items-start justify-between gap-3 px-4 py-2.5" data-id="element-823" data-mp="1kjg31q69b9zh">
                <div className="flex min-w-0 items-start gap-2.5" data-id="element-824" data-mp="rxmxj8tkth90">
                  <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${statusClasses[stakeholder.status]}`} aria-hidden="true" data-id="element-825" data-mp="16l7z2yasz8et" />
                  <div className="min-w-0" data-id="element-826" data-mp="11sy9xkhiicdl">
                    <p className="text-[13px] text-foreground" data-id="element-827" data-mp="e45bnhdf3sft">{stakeholder.body}</p>
                    <p className="text-[11px] text-muted-foreground" data-id="element-828" data-mp="96luo9pc51k6">
                      {stakeholder.representative}
                    </p>
                  </div>
                </div>
                <span className="shrink-0 text-[11px] text-muted-foreground" data-id="element-829" data-mp="2pjw51u7lvwyg">
                  {stakeholder.status === 'signed' && stakeholder.on ? `Signed ${formatDate(stakeholder.on)}` : stakeholder.status === 'pending' ? 'Pending' : 'Blocked'}
                </span>
              </li>;
        })}
        </ul>
      </SectionCard>

      <SectionCard title="Conditions attached" description="Conditions carried forward into the next stage" data-id="element-830" data-mp="38hyu2905ltw9">
        {approval.conditions.length === 0 ? <p className="text-[12px] text-muted-foreground" data-id="element-831" data-mp="2zhk9xcyxekuc">
            No conditions attached to this approval.
          </p> : <ul className="space-y-2" data-id="element-832" data-mp="1j2sf6teb51hv">
            {approval.conditions.map(condition => <li key={condition} className="rounded-sm border border-border bg-muted/50 px-3 py-2 text-[12px] leading-relaxed" data-id="element-833" data-mp="1o4rjq6lsa4ge">
                {condition}
              </li>)}
          </ul>}
      </SectionCard>
    </div>;
}